import { useCallback, useEffect, useRef, useState } from 'react';
import api from '@/shared/api/client';
import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import { toast } from 'sonner';
import { Loader2, Unplug, Wifi, WifiOff, Check, RefreshCw, X as XIcon } from 'lucide-react';
import ConfirmDialog from '@/shared/components/ConfirmDialog';

/**
 * Rattraper un matériel après coup : l'écran PROPOSE, l'humain décide.
 *
 * POURQUOI CET ÉCRAN EXISTE
 *
 * Depuis le 30/08, le parcours d'installation n'attend plus qu'un appareil réponde pour
 * avancer (décision de David). Un chantier peut donc se terminer avec un équipement qui
 * n'a jamais répondu : mauvaise étiquette recopiée, batterie pas encore branchée,
 * thermostat pas encore appairé. Le dossier part « en attente de matériel », il est
 * listé et compté côté poseur — et c'est ici qu'on le répare.
 *
 * CE QUE LE RATTRAPAGE PAR ADRESSE COURRIEL NE PEUT PAS FAIRE
 *
 * L'idée de départ était de croiser les adresses courriel. Mesuré le 30/08 en appelant
 * l'API Tuya : `GET /v1.0/users` répond « could not find the oem saas info ». Notre
 * projet n'est pas un projet OEM/SaaS, et Tuya ne nous donne jamais le courriel de
 * personne. Le pivot par courriel n'existe pas — cet écran repose donc sur les deux
 * pivots qui existent : les appareils que voient nos utilisateurs associés (Tuya), et
 * les numéros de série que les machines annoncent d'elles-mêmes (broker MQTT).
 *
 * ⚠️ CET ÉCRAN N'ATTACHE JAMAIS TOUT SEUL. Se tromper de client attribue à quelqu'un la
 * production d'un autre, donc sa rémunération. Ouvrir la page n'écrit rien ; seul un
 * clic sur « Rattacher » relie, et le serveur réinterroge la machine à ce moment-là.
 *
 * UN APPAREIL ÉTEINT SE RATTACHE, ET C'EST LE CAS COURANT DU TERRAIN. Le serveur ne
 * refuse que ce qui ne prouve pas l'existence de la machine (numéro inconnu, lien au
 * tiers cassé, appareil déjà pris) — pas son silence. Un thermostat posé mais pas
 * encore sous tension est reconnu par Tuya : il se rattache, et la fiche se remplira
 * à son allumage.
 *
 * Ce rattachement-là passe par une confirmation explicite, parce que les deux succès
 * ne se valent pas : « rattaché » sur un appareil muet laisserait partir celui qui a
 * cliqué en croyant que les relevés arrivent. La confirmation dit ce qu'il obtient
 * AVANT le clic, et le toast le redit après.
 *
 * « REVÉRIFIER » REPOSE LA QUESTION SUR LE NUMÉRO DÉJÀ SAISI (lot L2, retour de David du
 * 16/10). Les propositions sont des appareils ORPHELINS : le numéro que le dossier porte
 * n'en est pas un, et la PAC du showroom de Tremblay, que Tuya disait en ligne, restait
 * ici sans rien à proposer. Le serveur réinterroge ce numéro avec les mêmes verdicts
 * que « Rattacher ».
 *
 * L'ÉCART DE CARACTÈRES EST LE MEILLEUR INDICE, ET IL EST MESURÉ. En pré-production,
 * une batterie enrôlée sous `28045340019` publie en réalité sous `280425340019` : un
 * « 2 » manquant. C'est ce qui remonte la bonne proposition en tête.
 */
export default function RattrapageMaterielPage() {
  const [lignes, setLignes] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [occupe, setOccupe] = useState('');
  // Par dossier : les appareils d'un AUTRE type sont repliés au départ. Repliés, pas
  // supprimés — le principe d'origine tient, c'est l'humain qui tranche.
  const [deplies, setDeplies] = useState({});
  const [confirmPending, setConfirmPending] = useState(null);
  // Le verrou de « Revérifier » est une référence : deux clics dans le même rendu
  // verraient encore `occupe` vide, et chacun coûterait une question au tiers.
  const reverificationEnCours = useRef(false);

  const charger = useCallback(() => {
    setChargement(true);
    api.get('/rattrapage-materiel/propositions')
      .then(r => setLignes(r.data || []))
      .catch(() => setLignes([]))
      .finally(() => setChargement(false));
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const executerRattachement = async (assetId, identifiant) => {
    setOccupe(`${assetId}:${identifiant}`);
    try {
      const { data } = await api.post('/rattrapage-materiel/rattacher',
        { asset_id: assetId, identifiant });
      // LES DEUX SUCCÈS NE SE DISENT PAS PAREIL. Le serveur rend `vivant` : sans lui,
      // un rattachement sur machine éteinte s'annoncerait comme un rattachement sur
      // machine qui parle, et personne ne comprendrait pourquoi la fiche reste vide.
      if (data?.vivant === false) {
        toast.warning(
          `Appareil ${identifiant} rattaché, mais éteint — les relevés remonteront `
          + `à sa mise sous tension.`, { duration: 12000 });
      } else {
        toast.success(`Appareil ${identifiant} rattaché.`);
      }
      charger();
    } catch (e) {
      // Le refus du serveur est le message le plus utile de l'écran : il dit POURQUOI
      // le rattachement n'a pas eu lieu (appareil muet, déjà pris par un autre
      // dossier). Le remplacer par « Erreur » ferait recliquer indéfiniment.
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail
        : detail?.message || "Le rattachement n'a pas eu lieu.");
    }
    setOccupe('');
  };

  /**
   * Un appareil qui répond se rattache d'un clic ; un appareil muet passe par une
   * confirmation. Le supplément de geste est proportionné au supplément d'incertitude :
   * on ne peut pas vérifier sur-le-champ que c'est le bon boîtier, puisqu'il se tait.
   */
  const demanderRattachement = (ligne, proposition) => {
    if (proposition.en_ligne) {
      executerRattachement(ligne.asset_id, proposition.identifiant);
      return;
    }
    setConfirmPending({
      title: 'Rattacher un appareil éteint',
      targetLabel: 'Appareil ciblé',
      target: `${proposition.identifiant} — `
        + `${proposition.canal === 'mqtt' ? 'Broker MQTT' : 'Cloud Tuya'}`
        + `${proposition.modele ? ` · ${proposition.modele}` : ''}`,
      message: `Il sera rattaché au dossier ${ligne.client_nom || '(client sans nom)'}`
        + `${ligne.ville ? ` · ${ligne.ville}` : ''}.`,
      warning: "L'appareil est reconnu, mais il ne répond pas en ce moment. Le "
        + "rattachement est enregistré ; la fiche restera sans relevé jusqu'à sa mise "
        + "sous tension, et le dossier restera listé ici en attendant.",
      confirmLabel: 'Rattacher quand même',
      onConfirm: () => executerRattachement(ligne.asset_id, proposition.identifiant),
    });
  };

  /**
   * Un refus recharge AUSSI la liste : le serveur y a écrit le dernier verdict et sa
   * date, que la ligne affiche sous le numéro saisi.
   */
  const reverifier = async (ligne) => {
    // Un geste à la fois sur l'écran : `occupe` est une seule valeur, un second clic
    // pendant l'appel l'écraserait et réactiverait le premier bouton.
    if (occupe || reverificationEnCours.current) return;
    reverificationEnCours.current = true;
    setOccupe(`${ligne.asset_id}:reverifier`);
    try {
      const { data } = await api.post('/rattrapage-materiel/reverifier', { asset_id: ligne.asset_id });
      if (data?.vivant === false) {
        toast.warning(`Appareil ${ligne.identifiant_saisi} reconnu mais éteint : rattaché, les `
          + `relevés remonteront à sa mise sous tension.`, { duration: 12000 });
      } else {
        toast.success(`Appareil ${ligne.identifiant_saisi} vérifié : il répond, le dossier est rattrapé.`);
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail
        : detail?.message || "La revérification n'a pas eu lieu.");
    }
    reverificationEnCours.current = false;
    setOccupe('');
    charger();
  };

  const ecarter = async (assetId, identifiant) => {
    setOccupe(`${assetId}:${identifiant}`);
    try {
      await api.post('/rattrapage-materiel/ecarter', { asset_id: assetId, identifiant });
      toast.success('Proposition écartée. Rien n’a été rattaché.');
      charger();
    } catch {
      toast.error("La proposition n'a pas pu être écartée.");
    }
    setOccupe('');
  };

  return (
    <div className="p-6 space-y-5" data-testid="rattrapage-materiel-page">
      <div>
        <h1 className="text-xl font-semibold text-neutral-900">Rattrapage de matériel</h1>
        <p className="text-[13px] text-neutral-500 mt-1">
          Les dossiers dont un équipement n’a jamais répondu, et les appareils qu’aucun
          dossier ne porte. L’écran propose un rapprochement&nbsp;; c’est vous qui
          décidez, et rien n’est rattaché tant que vous n’avez pas confirmé.
        </p>
      </div>

      {chargement && (
        <p className="text-[13px] text-neutral-400 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Lecture des trois canaux…
        </p>
      )}

      {!chargement && lignes.length === 0 && (
        <p className="text-[13px] text-neutral-500" data-testid="rattrapage-vide">
          Aucun dossier n’attend de matériel. Tous les équipements enrôlés répondent.
        </p>
      )}

      {lignes.map(ligne => (
        <div key={ligne.asset_id}
             className="rounded-lg border border-neutral-200 bg-white p-4 space-y-3"
             data-testid={`rattrapage-actif-${ligne.asset_id}`}>
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="font-medium text-neutral-900 text-[14px]">
                {ligne.client_nom || '(client sans nom)'}
                <span className="text-neutral-400 font-normal"> · {ligne.ville}</span>
              </p>
              <p className="text-[12px] text-neutral-500">
                {ligne.type}
                {ligne.reference_chantier ? ` · Réf. ${ligne.reference_chantier}` : ''}
              </p>
            </div>
            <Badge variant="outline" className="text-[11px] gap-1">
              <Unplug className="w-3 h-3" /> en attente de matériel
            </Badge>
          </div>

          {/* CE QUI A ÉTÉ TAPÉ SUR LE CHANTIER, sous les yeux. Sans lui, personne ne
              peut juger si une proposition à un caractère près est la bonne. */}
          <div className="rounded-md bg-neutral-50 border border-neutral-200 p-3 text-[12px]">
            <span className="text-neutral-500">Saisi sur le chantier :</span>{' '}
            <span className="font-mono font-medium text-neutral-900">
              {ligne.identifiant_saisi || '—'}
            </span>
            {ligne.identifiant_saisi
              ? ` (${ligne.identifiant_saisi.length} caractères)` : ''}
            {ligne.enrolement?.message
              ? <span className="block text-neutral-500 mt-0.5">{ligne.enrolement.message}</span>
              : null}
            {ligne.identifiant_saisi && (
              <div className="mt-2">
                <Button size="sm" variant="outline" className="h-8 text-[12px]"
                        disabled={!!occupe}
                        onClick={() => reverifier(ligne)}
                        data-testid={`reverifier-${ligne.asset_id}`}>
                  {occupe === `${ligne.asset_id}:reverifier`
                    ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Vérification…</>
                    : <><RefreshCw className="w-3.5 h-3.5 mr-1" /> Revérifier ce numéro</>}
                </Button>
              </div>
            )}
          </div>

          {ligne.propositions.length === 0 ? (
            <p className="text-[12px] text-neutral-500">
              Aucun appareil libre à proposer pour l’instant. Un appareil devient
              proposable dès qu’il s’annonce sur le broker ou qu’il apparaît chez nos
              utilisateurs Tuya associés.
              {ligne.identifiant_saisi
                ? ' Si le numéro saisi est le bon, « Revérifier ce numéro » interroge à nouveau l’appareil.'
                : ''}
            </p>
          ) : (
            <>
            <p className="text-[12px] text-neutral-500" data-testid={`rattrapage-explication-${ligne.asset_id}`}>
              Appareils libres qui pourraient être celui de ce chantier, du plus probable au
              moins probable. Rien n’est rattaché tant que vous n’avez pas cliqué sur
              « Rattacher ».
            </p>
            <ul className="space-y-2">
              {/* LES TYPES DIFFÉRENTS SONT REPLIÉS, JAMAIS SUPPRIMÉS.
                  Le classement les mettait déjà en dernier, mais ils s'affichaient tous :
                  une PAC Tuya proposée à un dossier PV est du bruit, et le bruit fait
                  cliquer à côté. Le principe d'origine tient — « aucun candidat n'est
                  caché, même lointain : c'est l'humain qui tranche » — donc on REPLIE.
                  Un clic les ramène, et le compte est écrit sur le bouton. */}
              {(deplies[ligne.asset_id]
                ? ligne.propositions
                : ligne.propositions.filter(p => p.type_concorde)).map(p => (
                <li key={p.identifiant}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-neutral-200 p-3"
                    data-testid={`proposition-${ligne.asset_id}-${p.identifiant}`}>
                  <div className="min-w-0">
                    <p className="font-mono text-[13px] font-medium text-neutral-900">{p.identifiant}</p>
                    <p className="text-[11px] text-neutral-500">
                      {p.canal === 'mqtt' ? 'Broker MQTT' : 'Cloud Tuya'}
                      {p.modele ? ` · ${p.modele}` : ''}
                      {p.type_suggere ? ` · ${p.type_suggere}` : ''}
                    </p>
                    {/* ⚠️ CHAQUE MENTION DIT CE QU'ELLE COMPARE. Retour de David en vidéo, le
                        24/09 : « aussi proposé à 10 autres dossiers, c'est quoi ça ? on sait
                        pas ». « type concordant » et « 19 caractères d'écart » ne disaient
                        pas davantage à QUOI l'appareil était comparé. La référence est
                        toujours l'équipement du chantier et le numéro saisi au-dessus. */}
                    <p className="text-[11px] mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                      <span className={p.type_concorde ? 'text-emerald-700' : 'text-neutral-500'}>
                        {p.type_concorde ? 'même type que l’équipement du chantier' : 'autre type d’équipement'}
                      </span>
                      <span className={p.ecart_caracteres <= 2 ? 'text-emerald-700' : 'text-neutral-500'}>
                        {p.ecart_caracteres === 0
                          ? 'numéro identique à la saisie'
                          : `numéro différent de la saisie sur ${p.ecart_caracteres} caractère${p.ecart_caracteres > 1 ? 's' : ''}`}
                      </span>
                      <span className="flex items-center gap-1 text-neutral-500">
                        {p.en_ligne
                          ? <><Wifi className="w-3 h-3 text-emerald-600" /> en ligne</>
                          : <><WifiOff className="w-3 h-3" /> ne répond pas</>}
                      </span>
                      {/* Le même appareil peut légitimement être proposé à plusieurs
                          dossiers muets. Ce qui ne va pas, c'est de ne pas le dire :
                          rattacher ici RETIRE la proposition là-bas, et personne ne
                          pouvait le savoir avant de cliquer. Relevé du 31/08, capture
                          à l'appui — `bf439ea1b8c646ee82itix` sous deux dossiers. */}
                      {p.dossiers_concurrents > 0 && (
                        <span className="text-amber-700"
                              data-testid={`concurrent-${p.identifiant}`}>
                          {p.dossiers_concurrents > 1
                            ? `aussi candidat pour ${p.dossiers_concurrents} autres chantiers : le rattacher ici le retire des autres`
                            : 'aussi candidat pour 1 autre chantier : le rattacher ici le retire de l’autre'}
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" className="h-8 text-[12px]"
                            disabled={!!occupe}
                            onClick={() => ecarter(ligne.asset_id, p.identifiant)}
                            data-testid={`ecarter-${ligne.asset_id}-${p.identifiant}`}>
                      <XIcon className="w-3.5 h-3.5 mr-1" /> Écarter
                    </Button>
                    <Button size="sm" className="h-8 text-[12px]"
                            disabled={!!occupe}
                            onClick={() => demanderRattachement(ligne, p)}
                            data-testid={`rattacher-${ligne.asset_id}-${p.identifiant}`}>
                      <Check className="w-3.5 h-3.5 mr-1" /> Rattacher
                    </Button>
                  </div>
                </li>
              ))}
              {(() => {
                const autres = ligne.propositions.filter(p => !p.type_concorde).length;
                if (!autres) return null;
                const ouvert = !!deplies[ligne.asset_id];
                return (
                  <li>
                    <button type="button"
                            className="text-[12px] text-marque underline"
                            onClick={() => setDeplies(d => ({ ...d, [ligne.asset_id]: !ouvert }))}
                            data-testid={`deplier-${ligne.asset_id}`}>
                      {ouvert
                        ? `Masquer les ${autres} appareil${autres > 1 ? 's' : ''} d’un autre type`
                        : `Voir aussi ${autres} appareil${autres > 1 ? 's' : ''} d’un autre type`}
                    </button>
                  </li>
                );
              })()}
              {ligne.propositions.length > 0
                && ligne.propositions.every(p => !p.type_concorde)
                && !deplies[ligne.asset_id] && (
                <li className="text-[12px] text-neutral-500" data-testid={`aucun-concordant-${ligne.asset_id}`}>
                  Aucun appareil du même type n’est libre. Ceux d’un autre type restent
                  consultables ci-dessous.
                </li>
              )}
            </ul>
            </>
          )}
        </div>
      ))}

      <ConfirmDialog
        pending={confirmPending}
        busy={!!occupe}
        onCancel={() => setConfirmPending(null)}
        onConfirm={() => {
          // On referme AVANT d'exécuter : sinon un second clic pendant l'appel
          // rejouerait le rattachement, et le journal porterait deux lignes pour un
          // seul geste.
          const action = confirmPending?.onConfirm;
          setConfirmPending(null);
          if (action) action();
        }}
      />
    </div>
  );
}
